import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Search } from '@mui/icons-material';
import { useAllPermissions, usePermission } from '../hooks';
import { colors } from '../theme/theme';

export const PermissionsPage = () => {
  const { can } = usePermission();
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const { permissions, isLoading, error } = useAllPermissions();

  const filteredPermissions = permissions.filter((permission: any) =>
    !search ||
    permission.name?.toLowerCase().includes(search.toLowerCase()) ||
    permission.module?.toLowerCase().includes(search.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Permission Name',
      flex: 1,
      minWidth: 220,
    },
    {
      field: 'module',
      headerName: 'Module',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value || 'General'}
          size="small"
          sx={{ textTransform: 'capitalize', fontWeight: 500 }}
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: any) => value || 'N/A',
    },
    {
      field: 'roles',
      headerName: 'Assigned Roles',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          {params.value?.length > 0 ? params.value.map((role: any) => (
            <Chip
              key={role.id}
              label={role.name}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          )) : (
            <Chip label="No roles" size="small" sx={{ color: colors.grey[500] }} />
          )}
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Permissions"
          subtitle="View and manage system permissions"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Permissions' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load permissions. Please try again.
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search permissions..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPaginationModel(prev => ({ ...prev, page: 0 })); }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={filteredPermissions}
                  columns={columns}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[10, 25, 50, 100]}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
